/**
 * RetentionPolicyModal (Spec 201) — create or edit a GFS retention policy.
 *
 * Proper modal entry (no inline editing). Explains keep-counts, GFS tiers, and the grace window.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { Modal, Button, Input, Select, Checkbox } from '@/components/ui';
import { retentionApi, GfsRetentionPolicy } from '../../api/retention';

interface Props {
  open: boolean;
  policy: GfsRetentionPolicy | null; // null = create
  onClose: () => void;
}

type NumKey = 'keepFull' | 'keepIncremental' | 'gfsDaily' | 'gfsWeekly' | 'gfsMonthly' | 'gfsYearly' | 'graceDays';
const NUM_FIELDS: { key: NumKey; label: string; hint: string }[] = [
  { key: 'keepFull', label: 'Keep full backups', hint: 'How many of the most recent FULL backups to always keep.' },
  { key: 'keepIncremental', label: 'Keep incremental backups', hint: 'How many of the most recent incremental backups to keep.' },
  { key: 'gfsDaily', label: 'Daily (GFS)', hint: 'Keep one backup per day for this many days.' },
  { key: 'gfsWeekly', label: 'Weekly (GFS)', hint: 'Keep one backup per week for this many weeks.' },
  { key: 'gfsMonthly', label: 'Monthly (GFS)', hint: 'Keep one backup per month for this many months.' },
  { key: 'gfsYearly', label: 'Yearly (GFS)', hint: 'Keep one backup per year for this many years.' },
  { key: 'graceDays', label: 'Grace window (days)', hint: 'After a backup is pruned it is soft-deleted; it is permanently purged this many days later.' },
];

const str = (v: number | null | undefined): string => (v == null ? '' : String(v));

export const RetentionPolicyModal: React.FC<Props> = ({ open, policy, onClose }) => {
  const qc = useQueryClient();
  const isEdit = !!policy;
  const [name, setName] = useState('');
  const [appliesTo, setAppliesTo] = useState<string>(''); // '' = any
  const [isDefault, setIsDefault] = useState(false);
  const [nums, setNums] = useState<Record<NumKey, string>>({
    keepFull: '', keepIncremental: '', gfsDaily: '', gfsWeekly: '', gfsMonthly: '', gfsYearly: '', graceDays: '7',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (policy) {
      setName(policy.name);
      setAppliesTo(policy.appliesToEntityType ?? '');
      setIsDefault(policy.isDefault);
      setNums({
        keepFull: str(policy.keepFull), keepIncremental: str(policy.keepIncremental),
        gfsDaily: str(policy.gfsDaily), gfsWeekly: str(policy.gfsWeekly), gfsMonthly: str(policy.gfsMonthly),
        gfsYearly: str(policy.gfsYearly), graceDays: str(policy.graceDays),
      });
    } else {
      setName(''); setAppliesTo(''); setIsDefault(false);
      setNums({ keepFull: '', keepIncremental: '', gfsDaily: '', gfsWeekly: '', gfsMonthly: '', gfsYearly: '', graceDays: '7' });
    }
  }, [open, policy]);

  const numOrNull = (v: string): number | null => (v.trim() === '' ? null : Number(v));

  const save = async () => {
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: Partial<GfsRetentionPolicy> = {
        name: name.trim(),
        appliesToEntityType: (appliesTo || null) as GfsRetentionPolicy['appliesToEntityType'],
        isDefault,
        keepFull: numOrNull(nums.keepFull),
        keepIncremental: numOrNull(nums.keepIncremental),
        gfsDaily: numOrNull(nums.gfsDaily),
        gfsWeekly: numOrNull(nums.gfsWeekly),
        gfsMonthly: numOrNull(nums.gfsMonthly),
        gfsYearly: numOrNull(nums.gfsYearly),
        graceDays: numOrNull(nums.graceDays) ?? 7,
      };
      if (policy) await retentionApi.updatePolicy(policy.id, body);
      else await retentionApi.createPolicy(body);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['backup', 'retention-policies'] }),
        qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] }),
      ]);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the policy.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={isEdit ? `Edit retention policy — ${policy?.name}` : 'New retention policy'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create policy'}</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-accent)]" />
          <p>
            A retention policy decides <strong>how long backup copies are kept</strong>. <strong>Keep</strong> counts always
            retain the most recent N backups. <strong>GFS</strong> (grandfather-father-son) keeps one recovery point per
            day / week / month / year for long-term coverage without storing every backup. The <strong>grace window</strong>
            is how long a pruned backup is recoverable from the recycle bin before it is permanently purged. Assign a policy
            to each lakehouse/warehouse on the Backups tab; blank fields are simply not used.
          </p>
        </div>

        {error && <p className="rounded-md border border-[var(--color-status-danger)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm text-[var(--color-status-danger)]">{error}</p>}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Policy name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Prod — 7y compliance" />
          <Select
            label="Applies to"
            value={appliesTo}
            onChange={(e) => setAppliesTo(e.target.value)}
            options={[
              { value: '', label: 'Any entity type' },
              { value: 'lakehouse', label: 'Lakehouse only' },
              { value: 'warehouse', label: 'Warehouse only' },
            ]}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {NUM_FIELDS.map((f) => (
            <Input
              key={f.key}
              label={f.label}
              type="number"
              min="0"
              value={nums[f.key]}
              onChange={(e) => setNums((s) => ({ ...s, [f.key]: e.target.value }))}
              title={f.hint}
            />
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm text-[var(--color-text-primary)]">
          <Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Default policy for its entity type (used when a source has no policy assigned)
        </label>
      </div>
    </Modal>
  );
};

export default RetentionPolicyModal;
